import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import Sankey from 'highcharts/modules/sankey';
import { Tab } from '@hydrodigital/design-system';
import { PolygonLayer } from '@deck.gl/layers';
import { Popup } from 'react-map-gl';
import DmaService from '@/domain/Dma/DmaService';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';

Sankey(Highcharts);
HighchartsMore(Highcharts);

type MockMnfLinePoint = {
	timestamp: string;
	value: number;
};

type MockMnfRangePoint = {
	timestamp: string;
	min: number;
	max: number;
};

type MockMnfAnomalyPoint = {
	timestamp: string;
	value: number;
};

type MockMnfLegendItem = {
	key: 'anomaly' | 'alertThreshold' | 'expectedRange' | 'baseline' | 'observed';
	label: string;
	type: 'scatter' | 'line' | 'arearange';
	color: string;
};

type MockMnfResponse = {
	chart: {
		unit: string;
		xAxisTickDates: string[];
		yAxis: {
			title: string;
			min: number;
			max: number;
			tickInterval: number;
		};
	};
	legend: MockMnfLegendItem[];
	series: {
		observed: MockMnfLinePoint[];
		baseline: MockMnfLinePoint[];
		alertThreshold: MockMnfLinePoint[];
		expectedRange: MockMnfRangePoint[];
		anomalies: MockMnfAnomalyPoint[];
	};
};

const MNF_MOCK_START_DATE = Date.UTC(2024, 10, 1);
const MNF_DAY_MS = 24 * 60 * 60 * 1000;
const toMockMnfTimestamp = (index: number) =>
	new Date(MNF_MOCK_START_DATE + index * MNF_DAY_MS).toISOString();

const mockObservedValues = [
	14, 11, 15, 12, 12, 10, 16, 12, 15, 14,
	15, 18, 17, 12, 15, 12, 12, 15, 17, 12,
	11, 15, 18, 19, 16, 16, 16, 18, 15, 16,
	15, 12, 13, 12, 11, 16, 15, 14, 12, 12,
	18, 14, 15, 16, 8, 15, 15, 19, 10, 19,
	20, 12, 25, 9, 18,
];

const mockMnfResponse: MockMnfResponse = {
	chart: {
		unit: 'L/s',
		xAxisTickDates: [
			'2024-11-03T00:00:00.000Z',
			'2024-11-17T00:00:00.000Z',
			'2024-12-01T00:00:00.000Z',
			'2024-12-15T00:00:00.000Z',
			'2024-12-29T00:00:00.000Z',
		],
		yAxis: {
			title: 'Cauda (L/s)',
			min: 0,
			max: 35,
			tickInterval: 5,
		},
	},
	legend: [
		{ key: 'anomaly', label: 'Anomalia persistente', type: 'scatter', color: '#EF4444' },
		{ key: 'alertThreshold', label: 'Umbral de alerta', type: 'line', color: '#FCA5A5' },
		{ key: 'expectedRange', label: 'Rango esperado (P10-P90)', type: 'arearange', color: '#CBD5E1' },
		{ key: 'baseline', label: 'Baseline', type: 'line', color: '#FB923C' },
		{ key: 'observed', label: 'MNF observado', type: 'line', color: '#3B82F6' },
	],
	series: {
		observed: mockObservedValues.map((value, index) => ({
			timestamp: toMockMnfTimestamp(index),
			value,
		})),
		baseline: mockObservedValues.map((_, index) => ({
			timestamp: toMockMnfTimestamp(index),
			value: 15,
		})),
		alertThreshold: mockObservedValues.map((_, index) => ({
			timestamp: toMockMnfTimestamp(index),
			value: 19,
		})),
		expectedRange: mockObservedValues.map((value, index) => ({
			timestamp: toMockMnfTimestamp(index),
			min: Math.max(8, Math.min(value - 2.5, 12)),
			max: Math.max(Math.max(8, Math.min(value - 2.5, 12)) + 1.5, Math.min(value + 2.5, 18)),
		})),
		anomalies: [22, 23, 24].map((index) => ({
			timestamp: toMockMnfTimestamp(index),
			value: mockObservedValues[index],
		})),
	},
};

const normalizeMnfResponse = (response: any): MockMnfResponse => {
	const data = response?.data ?? response ?? {};
	const chartData = data?.chart ?? {};
	const xAxisTickDates =
		(Array.isArray(chartData?.xAxisTickDates) && chartData.xAxisTickDates) ||
		(Array.isArray(chartData?.xaxisTickDates) && chartData.xaxisTickDates) ||
		(Array.isArray(data?.xAxisTickDates) && data.xAxisTickDates) ||
		(Array.isArray(data?.xaxisTickDates) && data.xaxisTickDates) ||
		mockMnfResponse.chart.xAxisTickDates;
	const chartYAxis =
		chartData?.yAxis ??
		chartData?.yaxis ??
		data?.yAxis ??
		data?.yaxis ??
		{};

	return {
		chart: {
			unit: chartData?.unit ?? mockMnfResponse.chart.unit,
			xAxisTickDates,
			yAxis: {
				title: chartYAxis?.title ?? mockMnfResponse.chart.yAxis.title,
				min: Number.isFinite(chartYAxis?.min)
					? chartYAxis.min
					: mockMnfResponse.chart.yAxis.min,
				max: Number.isFinite(chartYAxis?.max)
					? chartYAxis.max
					: mockMnfResponse.chart.yAxis.max,
				tickInterval: Number.isFinite(chartYAxis?.tickInterval)
					? chartYAxis.tickInterval
					: mockMnfResponse.chart.yAxis.tickInterval,
			},
		},
		legend:
			Array.isArray(data?.legend) && data.legend.length
				? data.legend
				: mockMnfResponse.legend,
		series: {
			observed:
				Array.isArray(data?.series?.observed) && data.series.observed.length
					? data.series.observed
					: mockMnfResponse.series.observed,
			baseline:
				Array.isArray(data?.series?.baseline) && data.series.baseline.length
					? data.series.baseline
					: mockMnfResponse.series.baseline,
			alertThreshold:
				Array.isArray(data?.series?.alertThreshold) && data.series.alertThreshold.length
					? data.series.alertThreshold
					: mockMnfResponse.series.alertThreshold,
			expectedRange:
				Array.isArray(data?.series?.expectedRange) && data.series.expectedRange.length
					? data.series.expectedRange
					: mockMnfResponse.series.expectedRange,
			anomalies:
				Array.isArray(data?.series?.anomalies) && data.series.anomalies.length
					? data.series.anomalies
					: mockMnfResponse.series.anomalies,
		},
	};
};

const parseDashboardDateToTimestamp = (value?: any) => {
	if (!value) return null;

	if (value instanceof Date) {
		const timestamp = value.getTime();
		return Number.isFinite(timestamp) ? timestamp : null;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === 'object' && typeof value?.toDate === 'function') {
		const parsedDate = value.toDate();
		const timestamp = parsedDate instanceof Date ? parsedDate.getTime() : NaN;
		return Number.isFinite(timestamp) ? timestamp : null;
	}

	if (typeof value === 'object' && typeof value?.toISOString === 'function') {
		const timestamp = new Date(value.toISOString()).getTime();
		return Number.isFinite(timestamp) ? timestamp : null;
	}

	if (typeof value !== 'string') {
		const timestamp = new Date(value).getTime();
		return Number.isFinite(timestamp) ? timestamp : null;
	}

	const normalizedValue = value.includes('T') ? value : value.replace(' ', 'T');
	const timestamp = new Date(normalizedValue).getTime();
	return Number.isFinite(timestamp) ? timestamp : null;
};

const buildMnfXAxisTickPositions = (
	startTimestamp: number | null,
	endTimestamp: number | null,
	apiTickDates: string[],
) => {
	const apiTicks = apiTickDates
		.map((value) => new Date(value).getTime())
		.filter((value) => Number.isFinite(value));

	if (startTimestamp == null || endTimestamp == null || endTimestamp <= startTimestamp) {
		return apiTicks;
	}

	const rangeInDays = Math.max(1, Math.ceil((endTimestamp - startTimestamp) / MNF_DAY_MS));
	let stepInDays = 1;

	if (rangeInDays > 7 && rangeInDays <= 14) {
		stepInDays = 2;
	} else if (rangeInDays > 14 && rangeInDays <= 31) {
		stepInDays = 5;
	} else if (rangeInDays > 31 && rangeInDays <= 90) {
		stepInDays = 14;
	} else if (rangeInDays > 90) {
		stepInDays = Math.max(30, Math.ceil(rangeInDays / 6));
	}

	const generatedTicks: number[] = [];
	for (let tick = startTimestamp; tick < endTimestamp; tick += stepInDays * MNF_DAY_MS) {
		generatedTicks.push(tick);
	}
	generatedTicks.push(endTimestamp);

	const boundedApiTicks = apiTicks.filter((value) => value >= startTimestamp && value <= endTimestamp);

	return Array.from(new Set([startTimestamp, ...boundedApiTicks, ...generatedTicks, endTimestamp])).sort(
		(left, right) => left - right,
	);
};

export function useDmaMode(params: {
	enabled: boolean;
	selectedLocation: any;
	selectedDate?: [string, string] | string[] | null;
	resetKey?: number;
	onHover?: (info: any) => void;
}) {
	const { enabled, selectedLocation, selectedDate, resetKey, onHover } = params;
	const serviceRef = useRef(new DmaService());
	const selectedDateRef = useRef<typeof selectedDate>(selectedDate);
	const [rings, setRings] = useState<[number, number][][] | null>(null);
	const [meta, setMeta] = useState<{ id?: number | string; name?: string } | null>(null);
	const [panelCoord, setPanelCoord] = useState<[number, number] | null>(null);
	const [showPanel, setShowPanel] = useState<boolean>(false);
	const [activeDmaId, setActiveDmaId] = useState<number | string | null>(null);
	const [details, setDetails] = useState<any | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [activeTabId, setActiveTabId] = useState('WATER_BALANCE');
	const [mnfData, setMnfData] = useState<MockMnfResponse>(mockMnfResponse);
	const [mnfLoading, setMnfLoading] = useState<boolean>(false);
	const lastRequestKeyRef = useRef<string | null>(null);
	const activeRequestIdRef = useRef(0);
	const activeMnfRequestIdRef = useRef(0);

	const formatDateTime = useCallback((value?: string) => {
		if (!value) return undefined;
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return undefined;
		return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
	}, []);

	useEffect(() => {
		selectedDateRef.current = selectedDate;
	}, [selectedDate]);

	useEffect(() => {
		// Always clear current DMA overlay/panel immediately on:
		// - location change
		// - DMA reset event
		// - disabling DMA
		setRings(null);
		setMeta(null);
		setShowPanel(false);
		setPanelCoord(null);
		setActiveDmaId(null);
		setDetails(null);
		setLoading(false);
		setActiveTabId('WATER_BALANCE');
		setMnfData(mockMnfResponse);
		setMnfLoading(false);
		lastRequestKeyRef.current = null;
		activeRequestIdRef.current += 1;
		activeMnfRequestIdRef.current += 1;

		if (!enabled || !selectedLocation) return;

		let cancelled = false;
		(async () => {
			try {
				const res: any = await serviceRef.current.getDMAPolygonData(selectedLocation);
				const geom = res?.geometry;
				let nextRings: [number, number][][] = [];
				if (geom?.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
					nextRings = (geom.coordinates as any[][][]).flat() as [number, number][][];
				} else if (geom?.type === 'Polygon' && Array.isArray(geom.coordinates)) {
					nextRings = geom.coordinates as [number, number][][];
				} else if (Array.isArray(geom?.coordinates)) {
					const c = geom.coordinates as any[];
					if (Array.isArray(c[0]) && typeof c[0][0] === 'number') {
						nextRings = [c as [number, number][]];
					}
				}
				if (!cancelled) {
					setRings(nextRings.length ? nextRings : null);
					if (res?.id != null || res?.name != null) {
						setMeta({ id: res?.id, name: String(res?.name ?? '') });
					} else {
						setMeta(null);
					}
				}
			} catch (e) {
				if (!cancelled) {
					setRings(null);
					setMeta(null);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [enabled, selectedLocation, resetKey]);

	const fetchDmaDetails = useCallback((
		dmaId: number | string | null | undefined,
		options?: { force?: boolean },
	) => {
		if (dmaId == null) return;
		const [rawStartDate, rawEndDate] = Array.isArray(selectedDateRef.current)
			? selectedDateRef.current
			: [];
		const startDate = formatDateTime(rawStartDate);
		const endDate = formatDateTime(rawEndDate);
		const requestKey = `${dmaId}__${startDate ?? ''}__${endDate ?? ''}`;

		if (!options?.force && requestKey === lastRequestKeyRef.current) return;

		lastRequestKeyRef.current = requestKey;
		const requestId = ++activeRequestIdRef.current;
		setDetails(null);
		setLoading(true);
		serviceRef.current
			.getDMADetailsData(dmaId, { startDate, endDate })
			.then((res: any) => {
				if (requestId !== activeRequestIdRef.current) return;
				setDetails(res);
			})
			.finally(() => {
				if (requestId === activeRequestIdRef.current) {
					setLoading(false);
				}
			});
	}, [formatDateTime]);

	const fetchDmaMnfAnalysis = useCallback((dmaId: number | string | null | undefined) => {
		if (dmaId == null) return;
		const [rawStartDate, rawEndDate] = Array.isArray(selectedDateRef.current)
			? selectedDateRef.current
			: [];
		const startDate = formatDateTime(rawStartDate);
		const endDate = formatDateTime(rawEndDate);

		const requestId = ++activeMnfRequestIdRef.current;
		setMnfLoading(true);

		serviceRef.current
			.getDMAMnfAnalysis(dmaId, { startDate, endDate })
			.then((res: any) => {
				if (requestId !== activeMnfRequestIdRef.current) return;
				setMnfData(normalizeMnfResponse(res));
			})
			.finally(() => {
				if (requestId === activeMnfRequestIdRef.current) {
					setMnfLoading(false);
				}
			});
	}, [formatDateTime]);

	useEffect(() => {
		const subscription = dashboardMapEventsService.getSubject().subscribe((event: any) => {
			if (event?.type !== 'datesApplied') return;
			if (!enabled || !showPanel || activeDmaId == null) return;

			window.setTimeout(() => {
				fetchDmaDetails(activeDmaId, { force: true });
				if (activeTabId === 'MNF') {
					fetchDmaMnfAnalysis(activeDmaId);
				}
			}, 0);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [activeDmaId, activeTabId, enabled, fetchDmaDetails, fetchDmaMnfAnalysis, showPanel]);

	const layers = useMemo(() => {
		if (!enabled || !rings || !rings.length) return [];
		return [
			new PolygonLayer({
				id: 'dma-boundary',
				data: rings.map((polygon) => ({
					polygon,
					dmaId: meta?.id,
					dmaName: meta?.name,
					_tooltipType: 'dma',
				})),
				getPolygon: (d: any) => d.polygon,
				stroked: true,
				filled: true,
				getFillColor: [0, 170, 136, 60],
				getLineColor: [0, 170, 136, 220],
				lineWidthMinPixels: 2,
				pickable: true,
				onHover,
			}),
		];
	}, [enabled, rings, meta?.id, meta?.name, onHover]);

	const onMapClick = useCallback((event: any) => {
		if (!enabled) return false;
		if (showPanel && (!event.picked || event.layer?.id !== 'dma-boundary')) {
			setShowPanel(false);
			setPanelCoord(null);
			setActiveDmaId(null);
			return false;
		}
		if (event.picked && event.layer?.id === 'dma-boundary') {
			const dmaId = event.object?.dmaId;
			setActiveDmaId(dmaId ?? null);
			setActiveTabId('WATER_BALANCE');
			setMnfData(mockMnfResponse);
			setMnfLoading(false);
			activeMnfRequestIdRef.current += 1;
			const ring = event.object?.polygon as [number, number][] | undefined;
			if (ring && ring.length) {
				let maxLng = -Infinity;
				let minLat = Infinity;
				let maxLat = -Infinity;
				for (const [lng, lat] of ring) {
					if (lng > maxLng) maxLng = lng;
					if (lat < minLat) minLat = lat;
					if (lat > maxLat) maxLat = lat;
				}
				const midLat = (minLat + maxLat) / 2;
				const nextCoord: [number, number] = [maxLng + 0.001, midLat];
				setPanelCoord((prev) => {
					if (!prev) return nextCoord;
					if (Math.abs(prev[0] - nextCoord[0]) > 1e-9 || Math.abs(prev[1] - nextCoord[1]) > 1e-9) {
						return nextCoord;
					}
					return prev;
				});
			} else if (event.coordinate) {
				const nextCoord: [number, number] = [event.coordinate[0], event.coordinate[1]];
				setPanelCoord((prev) => {
					if (!prev) return nextCoord;
					if (Math.abs(prev[0] - nextCoord[0]) > 1e-9 || Math.abs(prev[1] - nextCoord[1]) > 1e-9) {
						return nextCoord;
					}
					return prev;
				});
			}
			setShowPanel((prev) => (prev ? prev : true));
			fetchDmaDetails(dmaId, { force: true });
			return true;
		}
		return false;
	}, [enabled, fetchDmaDetails, showPanel]);

	const sankeyOptions = useMemo(() => {
		const LABEL_COLOR: Record<string, { label: string; color: string }> = {
			system_input: { label: 'System Input', color: '#1f4ed3' },
			authorized_consumption: { label: 'Authorized Consumption', color: '#3b82f6' },
			water_losses: { label: 'Water Losses', color: '#f59e0b' },
			billed_authorized: { label: 'Billed Metered', color: '#2563eb' },
			billed_metered: { label: 'Billed Metered', color: '#2563eb' },
			unbilled_authorized: { label: 'Billed Unmetered', color: '#a78bfa' },
			billed_unmetered: { label: 'Billed Unmetered', color: '#a78bfa' },
			real_losses: { label: 'Real Losses', color: '#ef4444' },
			apparent_losses: { label: 'Apparent Losses', color: '#10b981' },
		};
		const toTitle = (s: string) =>
			String(s || '')
				.replace(/[_-]+/g, ' ')
				.trim()
				.toLowerCase()
				.replace(/\b\w/g, (c) => c.toUpperCase());
		const fallbackColor = '#64748b';

		const rawNodes: any[] = Array.isArray(details?.sankey?.nodes) ? details!.sankey!.nodes : [];
		const rawLinks: any[] = Array.isArray(details?.sankey?.links) ? details!.sankey!.links : [];
		const wb = details?.waterBalance ?? {};

		const wbLinkValues: Record<string, number> = {
			'system_input→authorized_consumption': Number(wb.authorizedConsumption),
			'system_input→water_losses': Number(wb.nonRevenueWater ?? (Number(wb.systemInputVolume) - Number(wb.authorizedConsumption))),
			'authorized_consumption→billed_authorized': Number(wb.billedMeteredConsumption),
			'authorized_consumption→billed_metered': Number(wb.billedMeteredConsumption),
			'authorized_consumption→unbilled_authorized': Number(wb.billedUnmeteredWater ?? wb.unbilledConsumption),
			'authorized_consumption→billed_unmetered': Number(wb.billedUnmeteredWater ?? wb.unbilledConsumption),
			'water_losses→real_losses': Number(wb.realLosses),
			'water_losses→apparent_losses': Number(wb.apparentLosses),
		};

		const seen = new Set<string>();
		const nodes = rawNodes
			.map((n) => {
				const key = String(n?.id ?? '').trim();
				if (!key || seen.has(key)) return null;
				seen.add(key);
				const map = LABEL_COLOR[key];
				const label = map ? map.label : toTitle(key);
				const color = map ? map.color : fallbackColor;
				return { id: label, color };
			})
			.filter(Boolean) as Array<{ id: string; color: string }>;

		const labelById = new Map<string, string>();
		for (const n of rawNodes) {
			const key = String(n?.id ?? '').trim();
			if (!key) continue;
			const map = LABEL_COLOR[key];
			labelById.set(key, map ? map.label : toTitle(key));
		}

		const data: any[] = rawLinks
			.map((l) => {
				const s = String(l?.source ?? '').trim();
				const t = String(l?.target ?? '').trim();
				const apiValue = Number(l?.value ?? NaN);
				const v = Number.isFinite(apiValue) ? apiValue : (wbLinkValues[`${s}→${t}`] ?? NaN);
				const from = labelById.get(s) ?? toTitle(s);
				const to = labelById.get(t) ?? toTitle(t);
				if (!from || !to || !Number.isFinite(v)) return null;
				return [from, to, v];
			})
			.filter(Boolean) as any[];

		const hasDynamic = nodes.length > 0 && data.length > 0;
		const nodesFinal = hasDynamic
			? nodes
			: [
					{ id: 'System Input', color: '#1f4ed3' },
					{ id: 'Authorized Consumption', color: '#3b82f6' },
					{ id: 'Water Losses', color: '#f59e0b' },
					{ id: 'Billed Metered', color: '#2563eb' },
					{ id: 'Billed Unmetered', color: '#a78bfa' },
					{ id: 'Real Losses', color: '#ef4444' },
					{ id: 'Apparent Losses', color: '#10b981' },
			  ];
		const dataFinal = hasDynamic
			? data
			: [
					['System Input', 'Authorized Consumption', 60],
					['System Input', 'Water Losses', 40],
					['Authorized Consumption', 'Billed Metered', 52],
					['Authorized Consumption', 'Billed Unmetered', 8],
					['Water Losses', 'Real Losses', 28],
					['Water Losses', 'Apparent Losses', 12],
			  ];
		return {
			title: { text: '' },
			chart: { height: 220, margin: [10, 10, 10, 10] },
			credits: { enabled: false },
			tooltip: {
				backgroundColor: '#000000',
				borderColor: '#000000',
				borderRadius: 8,
				borderWidth: 0,
				headerFormat: '',
				shadow: false,
				style: {
					color: '#FFFFFF',
				},
				formatter: function () {
					const point = this.point as any;
					const fmt = (v: number) =>
						Number(v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
					if (!point?.fromNode) {
						const nodeName = point?.name ?? point?.id ?? '';
						const nodeValue = fmt(point?.sum ?? point?.options?.sum ?? 0);
						return `<b>${nodeName}: ${nodeValue} m³/day</b>`;
					}
					const fromName = point?.fromNode?.name ?? point?.from ?? '';
					const toName = point?.toNode?.name ?? point?.to ?? '';
					return `<b>${fromName} → ${toName}: ${fmt(point?.weight)} m³/day</b>`;
				},
			},
			series: [
				{
					type: 'sankey',
					keys: ['from', 'to', 'weight'],
					nodes: nodesFinal,
					data: dataFinal,
					dataLabels: { enabled: true, style: { textOutline: 'none', color: '#000000' } },
				},
			],
		} as Highcharts.Options;
	}, [details]);

	const mnfOptions = useMemo(() => {
		const [rawStartDate, rawEndDate] = Array.isArray(selectedDate) ? selectedDate : [];
		const startTimestamp = parseDashboardDateToTimestamp(rawStartDate);
		const endTimestamp = parseDashboardDateToTimestamp(rawEndDate);
		const legendByKey = Object.fromEntries(
			mnfData.legend.map((item) => [item.key, item]),
		) as Record<MockMnfLegendItem['key'], MockMnfLegendItem>;
		const xAxisTickPositions = buildMnfXAxisTickPositions(
			startTimestamp,
			endTimestamp,
			mnfData.chart.xAxisTickDates,
		);
		const observedSeries = mnfData.series.observed.map(({ timestamp, value }) => [
			new Date(timestamp).getTime(),
			value,
		]);
		const baselineSeries = mnfData.series.baseline.map(({ timestamp, value }) => [
			new Date(timestamp).getTime(),
			value,
		]);
		const alertThresholdSeries = mnfData.series.alertThreshold.map(({ timestamp, value }) => [
			new Date(timestamp).getTime(),
			value,
		]);
		const expectedRangeSeries = mnfData.series.expectedRange.map(({ timestamp, min, max }) => [
			new Date(timestamp).getTime(),
			min,
			max,
		]);
		const anomalySeries = mnfData.series.anomalies.map(({ timestamp, value }) => ({
			x: new Date(timestamp).getTime(),
			y: value,
		}));

		return {
			chart: {
				type: 'line',
				zoomType: 'x',
				panning: true,
				panKey: 'shift',
				backgroundColor: '#FFFFFF',
				height: 300,
				marginLeft: 54,
				marginTop: 52,
				marginBottom: 34,
				marginRight: 12,
				spacingTop: 12,
				spacingRight: 8,
				spacingBottom: 4,
				spacingLeft: 4,
				resetZoomButton: {
					position: {
						align: 'right',
						x: -10,
						y: 8,
					},
					theme: {
						fill: '#FFFFFF',
						stroke: '#0F4C81',
						'stroke-width': 1,
						r: 12,
						style: {
							color: '#0F4C81',
							fontSize: '11px',
							fontWeight: '600',
						},
						states: {
							hover: {
								fill: '#EFF6FF',
								style: {
									color: '#0F4C81',
								},
							},
						},
					},
				},
			},
			title: { text: '' },
			credits: { enabled: false },
			exporting: { enabled: false },
			xAxis: {
				type: 'datetime',
				title: { text: '' },
				min: startTimestamp ?? undefined,
				max: endTimestamp ?? undefined,
				tickPositions: xAxisTickPositions,
				lineWidth: 1,
				lineColor: '#CBD5E1',
				tickColor: '#CBD5E1',
				tickLength: 6,
				gridLineColor: '#EEF2F7',
				gridLineWidth: 1,
				tickWidth: 0,
				labels: {
					useHTML: true,
					y: 10,
					overflow: 'allow',
					crop: false,
					style: { color: '#94A3B8', fontSize: '10px', fontWeight: '600' },
					formatter: function () {
						const date = new Date(this.value as number);
						const monthDay = date.toLocaleDateString('en-US', {
							month: 'short',
							day: 'numeric',
						});
						const year = date.getUTCFullYear();
						if (this.isFirst) {
							return `<span style="display:inline-block;transform:translateX(50%)">${monthDay}</span>`;
						}
						if (this.isLast) {
							return `<span style="display:inline-block;transform:translateX(-50%)">${monthDay}</span>`;
						}
						return monthDay;
					},
				},
			},
			yAxis: {
				title: {
					text: mnfData.chart.yAxis.title,
					rotation: 270,
					margin: 12,
					x: -2,
					reserveSpace: true,
					style: { color: '#94A3B8', fontSize: '11px', fontWeight: '600' },
				},
				min: mnfData.chart.yAxis.min,
				max: mnfData.chart.yAxis.max,
				tickInterval: mnfData.chart.yAxis.tickInterval,
				lineWidth: 1,
				lineColor: '#CBD5E1',
				tickLength: 6,
				tickColor: '#CBD5E1',
				gridLineColor: '#EEF2F7',
				labels: {
					align: 'right',
					x: -1,
					reserveSpace: true,
					style: { color: '#93C5FD', fontSize: '12px', fontWeight: '600' },
				},
			},
			legend: {
				align: 'center',
				verticalAlign: 'top',
				layout: 'horizontal',
				width: 560,
				x: 18,
				y: 0,
				margin: 8,
				padding: 8,
				backgroundColor: '#FFFFFF',
				borderColor: '#E2E8F0',
				borderWidth: 1,
				borderRadius: 0,
				itemDistance: 10,
				itemMarginBottom: 0,
				itemMarginTop: 2,
				symbolWidth: 14,
				symbolHeight: 8,
				symbolRadius: 0,
				symbolPadding: 5,
				itemStyle: { color: '#64748B', fontSize: '10px', fontWeight: '600' },
			},
			tooltip: {
				shared: true,
				xDateFormat: '%b %e, %Y',
				valueSuffix: ` ${mnfData.chart.unit}`,
				valueDecimals: 2,
			},
			plotOptions: {
				series: {
					animation: false,
					marker: { enabled: false },
				},
			},
			series: [
				{
					type: 'scatter',
					name: legendByKey.anomaly.label,
					data: anomalySeries,
					color: legendByKey.anomaly.color,
					marker: {
						enabled: true,
						symbol: 'circle',
						radius: 4,
						lineColor: legendByKey.anomaly.color,
						lineWidth: 1,
						fillColor: legendByKey.anomaly.color,
					},
					dataLabels: {
						enabled: true,
						format: '✖',
						style: {
							color: legendByKey.anomaly.color,
							fontSize: '12px',
							textOutline: 'none',
						},
						y: -8,
					},
				},
				{
					type: 'line',
					name: legendByKey.alertThreshold.label,
					data: alertThresholdSeries,
					color: legendByKey.alertThreshold.color,
					dashStyle: 'ShortDot',
					lineWidth: 2,
				},
				{
					type: 'arearange',
					name: legendByKey.expectedRange.label,
					data: expectedRangeSeries,
					color: 'rgba(148, 163, 184, 0.18)',
					lineWidth: 1,
					lineColor: legendByKey.expectedRange.color,
					fillOpacity: 0.45,
					zIndex: 0,
				},
				{
					type: 'line',
					name: legendByKey.baseline.label,
					data: baselineSeries,
					color: legendByKey.baseline.color,
					lineWidth: 2,
				},
				{
					type: 'line',
					name: legendByKey.observed.label,
					data: observedSeries,
					color: legendByKey.observed.color,
					lineWidth: 2,
					marker: {
						enabled: true,
						radius: 2,
					},
				},
			],
		} as Highcharts.Options;
	}, [mnfData, selectedDate]);

	const panelElement = enabled && showPanel && panelCoord ? (
		<Popup
			longitude={panelCoord[0]}
			latitude={panelCoord[1]}
			anchor="left"
			offset={[12, 0]}
			closeOnClick={true}
			closeButton={false}
			onClose={() => {
				setShowPanel(false);
				setPanelCoord(null);
				setActiveDmaId(null);
			}}
			className="rounded-lg"
			style={{ maxWidth: '760px', width: 'auto', borderRadius: '14px', backgroundColor: '#F8FAFC' }}
		>
			<div className="px-4 pt-3 pb-2" style={{ minWidth: 680 }}>
				<div className="flex items-start justify-between mb-1">
					<div className="text-[#111827] font-bold text-[20px] leading-7">
						{details?.name ?? meta?.name ?? ''}
					</div>
					<button
						type="button"
						aria-label="Close popup"
						className="flex h-8 w-8 items-center justify-center rounded text-[#1F2937] text-[28px] leading-none hover:bg-[#EEF2F7]"
						onClick={() => {
							setShowPanel(false);
							setPanelCoord(null);
							setActiveDmaId(null);
							setActiveTabId('WATER_BALANCE');
						}}
					>
						×
					</button>
				</div>
				{loading ? (
					<div className="p-2">Loading...</div>
				) : (
					<div className="text-[#1C2A4E]">
						<div className="text-xs font-bold tracking-wide text-[#6B7280] uppercase mb-3">
							DMA Lens
						</div>
						<div className="flex flex-wrap gap-2 mb-3">
							<span className="inline-flex items-center rounded-full border border-[#CBD5E1] bg-[#F8FAFC] px-2.5 py-1 text-xs font-semibold text-[#6B7280]">
								Total Connections: {details?.dmaMetadata?.totalConnections ?? '—'}
							</span>
							<span className="inline-flex items-center rounded-full border border-[#CBD5E1] bg-[#F8FAFC] px-2.5 py-1 text-xs font-semibold text-[#6B7280]">
								Population: {details?.dmaMetadata?.population ?? '—'}
							</span>
							<span className="inline-flex items-center rounded-full border border-[#CBD5E1] bg-[#F8FAFC] px-2.5 py-1 text-xs font-semibold text-[#6B7280]">
								Total Mains Length: {details?.dmaMetadata?.totalMainsLength ?? '—'} km
							</span>
							<span className="inline-flex items-center rounded-full border border-[#CBD5E1] bg-[#F8FAFC] px-2.5 py-1 text-xs font-semibold text-[#6B7280]">
								Avg. Connection Length: {details?.dmaMetadata?.avgConnectionsLength ?? '—'} m
							</span>
						</div>
						<Tab
							tabs={[
								{
									id: 'WATER_BALANCE',
									label: 'WATER BALANCE',
								},
								{
									id: 'MNF',
									label: 'MNF',
								},
							]}
							orientation="horizontal"
							activeTab={activeTabId}
							onTabChange={(tabId: string) => {
								setActiveTabId(tabId);
								if (tabId === 'MNF') {
									fetchDmaMnfAnalysis(activeDmaId);
								}
							}}
						/>
						{activeTabId === 'WATER_BALANCE' ? (
							<div style={{ marginTop: 6 }}>
								<HighchartsReact
									key="water-balance-chart"
									highcharts={Highcharts}
									options={sankeyOptions}
									immutable
								/>
							</div>
						) : (
							<div className="mt-2 rounded-lg border border-[#E2E8F0] bg-white px-2 py-2">
								{mnfLoading ? (
									<div className="py-10 text-center text-sm font-medium text-[#64748B]">
										Loading MNF analysis...
									</div>
								) : (
									<HighchartsReact
										key="mnf-chart"
										highcharts={Highcharts}
										options={mnfOptions}
										immutable
									/>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		</Popup>
	) : null;

	return { layers, panelElement, onMapClick };
}

export default useDmaMode;
